import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { titleForPath } from "@/data/mobileNav";

export type Peer = {
  key: string;
  userId: string;
  name: string;
  role: string;
  device: "mobile" | "desktop";
  route: string;
  routeLabel: string;
  onlineAt: string;
  self: boolean;
};

export type ConnectionState = "connecting" | "live" | "offline";

type PresenceValue = {
  peers: Peer[];
  status: ConnectionState;
};

const PresenceContext = createContext<PresenceValue>({ peers: [], status: "connecting" });

/** Everyone (doctor + staff devices) sharing this doctor's workspace, live. */
export const usePresence = () => useContext(PresenceContext);

function deviceKind(): "mobile" | "desktop" {
  if (typeof window === "undefined") return "desktop";
  return window.matchMedia("(max-width: 767px)").matches ? "mobile" : "desktop";
}

function makeKey() {
  const existing = sessionStorage.getItem("presence-key");
  if (existing) return existing;
  const key = crypto.randomUUID();
  sessionStorage.setItem("presence-key", key);
  return key;
}

export function PresenceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [peers, setPeers] = useState<Peer[]>([]);
  const [status, setStatus] = useState<ConnectionState>("connecting");
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const selfKey = useRef<string>("");

  useEffect(() => {
    if (!user) {
      setPeers([]);
      setStatus("connecting");
      return;
    }
    selfKey.current = makeKey();

    const meta = (user.user_metadata ?? {}) as Record<string, string>;
    const name =
      [meta.first_name, meta.last_name].filter(Boolean).join(" ").trim() ||
      meta.full_name ||
      user.email?.split("@")[0] ||
      "Device";

    const channel = supabase.channel(`workspace:${user.id}`, {
      config: { presence: { key: selfKey.current } },
    });
    channelRef.current = channel;

    const sync = () => {
      const state = channel.presenceState<Record<string, unknown>>();
      const list: Peer[] = [];
      for (const [key, entries] of Object.entries(state)) {
        const raw = (entries as unknown as Record<string, unknown>[])[0] ?? {};
        list.push({
          key,
          userId: String(raw.user_id ?? ""),
          name: String(raw.name ?? "Device"),
          role: String(raw.role ?? "member"),
          device: raw.device === "mobile" ? "mobile" : "desktop",
          route: String(raw.route ?? "/"),
          routeLabel: String(raw.route_label ?? ""),
          onlineAt: String(raw.online_at ?? new Date().toISOString()),
          self: key === selfKey.current,
        });
      }
      list.sort((a, b) => Number(b.self) - Number(a.self) || a.name.localeCompare(b.name));
      setPeers(list);
    };

    channel
      .on("presence", { event: "sync" }, sync)
      .on("presence", { event: "join" }, sync)
      .on("presence", { event: "leave" }, sync)
      .subscribe(async (s) => {
        if (s === "SUBSCRIBED") {
          setStatus("live");
          await channel.track({
            user_id: user.id,
            name,
            role: "doctor",
            device: deviceKind(),
            route: window.location.pathname,
            route_label: titleForPath(window.location.pathname),
            online_at: new Date().toISOString(),
          });
        } else if (s === "CHANNEL_ERROR" || s === "TIMED_OUT" || s === "CLOSED") {
          setStatus("offline");
        }
      });

    return () => {
      channelRef.current = null;
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Broadcast the screen the device is currently on.
  useEffect(() => {
    const channel = channelRef.current;
    if (!channel || status !== "live") return;
    const t = setTimeout(() => {
      channel.track({
        user_id: user?.id,
        name: peers.find((p) => p.self)?.name ?? "Device",
        role: "doctor",
        device: deviceKind(),
        route: pathname,
        route_label: titleForPath(pathname),
        online_at: new Date().toISOString(),
      });
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, status]);

  // Browser-level connectivity fallback.
  useEffect(() => {
    const off = () => setStatus("offline");
    const on = () => setStatus((s) => (s === "offline" ? "connecting" : s));
    window.addEventListener("offline", off);
    window.addEventListener("online", on);
    return () => {
      window.removeEventListener("offline", off);
      window.removeEventListener("online", on);
    };
  }, []);

  const value = useMemo(() => ({ peers, status }), [peers, status]);
  return <PresenceContext.Provider value={value}>{children}</PresenceContext.Provider>;
}
